import React, {FC, useContext, useEffect, useState} from 'react';
import WorkExperienceItem from './work-experience-item';
import {Icons} from '../../../../assets';
import {ButtonLoader} from '../../../../components';
import {AccountContext, AccountContextInterface} from '../../../../contexts';
import {AccountOrganization, Organization} from '../../../../models/organization.model';
import {GroupedWorkExperienceByOrganization, WorkExperience} from '../../../../models/work-experience.model';
import {Tab} from '../../../../types';
import {parseEmploymentTenure} from '../../../../utils';

interface OrganizationCardProps {
  organization: AccountOrganization;
  onOrganizationClick: (organization: AccountOrganization, type: Tab.Creds | Tab.Reccos) => void;
}

const OrganizationCard: FC<OrganizationCardProps> = ({organization, onOrganizationClick}) => {
  const {accountService} = useContext<AccountContextInterface>(AccountContext);
  const [groupedWorkExperience, setGroupedWorkExperience] = useState<{organization: Organization; roles: WorkExperience[]} | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const fetchWorkExperiences = async () => {
    setLoading(true);
    try {
      const {data} = await accountService.getWorkExperiences(organization.accountId.id, organization.organization.id);
      if (data) {
        handleWorkExp(data);
      }
    } catch (error) {
      console.error('Error fetching work experiences:', error);
    } finally {
      setLoading(false);
    }
  };

  const groupJobsByOrganization = (roles: WorkExperience[]): GroupedWorkExperienceByOrganization => {
    return roles.reduce<GroupedWorkExperienceByOrganization>((acc, job) => {
      const orgId = job.organization.id;
      if (!acc[orgId]) {
        acc[orgId] = {organization: job.organization, roles: []};
      }
      acc[orgId].roles.push(job);
      return acc;
    }, {});
  };

  const handleWorkExp = (roles: WorkExperience[]): void => {
    const groupedWorkExperienceByOrganization = groupJobsByOrganization(roles);
    Object.values(groupedWorkExperienceByOrganization).forEach(group => {
      group.roles.sort((a, b) => {
        const tenureA = parseEmploymentTenure(b.employmentTenure);
        const tenureB = parseEmploymentTenure(a.employmentTenure);

        const startA = tenureA.startDate ? tenureA.startDate.getTime() : -Infinity;
        const startB = tenureB.startDate ? tenureB.startDate.getTime() : -Infinity;

        return startB - startA;
      });
    });
    setGroupedWorkExperience(Object.values(groupedWorkExperienceByOrganization)[0] || null);
  };

  useEffect(() => {
    fetchWorkExperiences();
  }, [organization]);

  return (
    <div className="border border-[#F2F2F2] items-center justify-between p-3 rounded-lg">
      <div className="flex justify-between">
        <h3 className="font-bold text-base text-black">{organization.organization.name}</h3>
        {organization.credCount > 0 && (
          <div className="flex gap-4 items-center">
            <div className="flex gap-2 items-center cursor-pointer" onClick={() => onOrganizationClick(organization, Tab.Creds)}>
              <img src={Icons.CRED_ICON} className="h-5 w-5" alt="Cred" />
              <p className="font-semibold text-xs text-[#2B2B2B]">{organization.credCount}</p>
            </div>
            <div className="flex gap-2 items-center cursor-pointer" onClick={() => onOrganizationClick(organization, Tab.Reccos)}>
              <img src={Icons.RECCO_ICON_V2} className="h-5 w-5" alt="Recco" />
              <p className="font-semibold text-xs text-[#2B2B2B]">{organization.reccoCount}</p>
            </div>
          </div>
        )}
      </div>
      {loading ? (
        <ButtonLoader />
      ) : (
        <div className="flex-col mt-2">
          {groupedWorkExperience?.roles
            ?.slice()
            .reverse()
            .map((item, index) => (
              <WorkExperienceItem key={item.id} item={item} index={index} total={groupedWorkExperience?.roles.length || 0} />
            ))}
        </div>
      )}
    </div>
  );
};

export default OrganizationCard;
